import { css } from '@emotion/react'

import { Coin, Bank, LineChart, PieChart } from '../icons'
import { numberToDollars } from '../../utils/utils'

// The four top-level asset classes, in display order, each mapped to the icon
// used in the current asset allocation implementation.
export const CATEGORY_ORDER = ['Stocks', 'Bonds', 'Cash', 'Misc']

// Copy shown for an account with no current balance: the recommended mix is a
// percentage-only allocation (no dollars, since there's nothing invested yet),
// introduced by this note. The apostrophe is a curly quote (U+2019), per the design.
export const NO_BALANCE_NOTE =
  'If you’re going to add money to this account within the next 30 days, use this investment mix.'

const iconCss = css`
  width: 100%;
  height: auto;
`

export const categoryIcon = name => {
  switch (name) {
    case 'Cash':
      return <Coin css={iconCss} />
    case 'Bonds':
      return <Bank css={iconCss} />
    case 'Stocks':
      return <LineChart css={iconCss} />
    case 'Misc':
    case 'Other':
      return <PieChart css={iconCss} />
    default:
      return null
  }
}

/** "12%" from a raw percent number; empty string for null/undefined. */
export const formatPercent = value =>
  value === null || value === undefined ? '' : `${numberToDollars(value, true)}%`

/** "$1,234" from a raw dollar number; empty string for null/undefined. */
export const formatDollar = value =>
  value === null || value === undefined ? '' : `$${numberToDollars(value, true)}`

/** "$1,234.56" from a raw dollar number; "$0" for zero; empty string for null/undefined. */
export const formatDollarCents = value => {
  if (value === null || value === undefined) {
    return ''
  }
  return Math.round(value * 100) === 0 ? '$0' : `$${numberToDollars(value)}`
}

/** Combined "12% | $1,234" label used beside each bar. */
export const formatValue = value => {
  if (!value) {
    return ''
  }
  const parts = []
  if (value.percent !== null && value.percent !== undefined) {
    parts.push(formatPercent(value.percent))
  }
  if (value.dollar !== null && value.dollar !== undefined) {
    parts.push(formatDollar(value.dollar))
  }
  return parts.join('  |  ')
}

/**
 * Ordered value labels for the desktop rows. The design leads the current
 * column with dollars ("$14,320.43 | 16%") and the recommended column with the
 * percentage ("6% | $5,367.16"), so the two read outward from the center rule.
 * `dollarless` drops the dollar figure entirely (percentage only), used for a
 * zero-balance account where every dollar amount would be "$0".
 */
const orderedValue = (value, dollarFirst, dollarless) => {
  if (!value) {
    return ''
  }
  const hasPercent = value.percent !== null && value.percent !== undefined
  const hasDollar = !dollarless && value.dollar !== null && value.dollar !== undefined
  const percent = hasPercent ? formatPercent(value.percent) : ''
  const dollar = hasDollar ? formatDollarCents(value.dollar) : ''
  const parts = (dollarFirst ? [dollar, percent] : [percent, dollar]).filter(Boolean)
  return parts.join('  |  ')
}

export const formatCurrentValue = value => orderedValue(value, true)
export const formatRecommendedValue = (value, dollarless) => orderedValue(value, false, dollarless)

/** Clamp a percent to the 0–100 range used for bar widths. */
export const barWidth = value => {
  const percent = value?.percent
  if (percent === null || percent === undefined || isNaN(percent)) {
    return 0
  }
  return Math.max(0, Math.min(100, percent))
}
